import type {ISbLinksParams} from "storyblok-js-client";
import {
	useAsyncDataWithPausing,
	type AsyncDataOptionsWithPausing,
} from "./use-async-data-with-pausing";

import type {MaybeRef} from "vue";
import {isRef, unref} from "vue";
import {useRuntimeConfig} from "nuxt/app";
import {useStoryblokApi} from "@storyblok/vue";

export type ReffedParameters = {
	[K in keyof ISbLinksParams]: MaybeRef<ISbLinksParams[K]>;
};

export const unrefParameters = (
	parameters: ReffedParameters,
): ISbLinksParams => {
	const concreteParameters: ISbLinksParams = {};
	for (const [key, value] of Object.entries(parameters)) {
		const typedKey = key as keyof typeof concreteParameters;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Storyblok query params are loosely typed
		concreteParameters[typedKey] = unref(value) as unknown as any;
	}
	return concreteParameters;
};

const defaultParameters = {
	per_page: 1000,
};

export interface StoryblokLink {
	id: number;
	slug: string;
	name: string;
	is_folder: boolean;
	is_startpage: boolean;
	uuid: string;
	real_path: string;
	position: number;
	updated_at: string;
	created_at: string;
	published_at: null | string;
}

export function useStoryblokLinks(
	fetchKey: string = "storyblok-links",
	parameters: ReffedParameters = {},
	asyncOptions: AsyncDataOptionsWithPausing<StoryblokLink[]> = {},
) {
	const finalParameters = {...defaultParameters, ...parameters};

	const api = useStoryblokApi();

	const watchValues = Object.values(finalParameters).filter((value) =>
		isRef(value),
	);

	return useAsyncDataWithPausing<StoryblokLink[]>(
		fetchKey,
		async () => {
			const linksRes = await api.getAll("cdn/links", {
				version:
					useRuntimeConfig().public.storyblokVersion === "published"
						? "published"
						: "draft",
				// https://www.storyblok.com/faq/how-to-load-more-than-100-stories-with-the-js-client
				paginated: 1,
				...unrefParameters(finalParameters),
			});
			return linksRes;
		},
		{
			watch: watchValues,
			...asyncOptions,
		},
	);
}
